import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    Plus,
    Search,
    Trash2,
    Edit,
    X,
    Check,
    Loader2,
    FileText,
    Server,
    RefreshCw,
    Layers,
    Cpu,
    Copy,
    UserCircle,
    Mic,
    PhoneCall,
    Building2,
    Boxes
} from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'
import SearchableDropdown from '../components/SearchableDropdown'
import { CloneModal, CloneResult } from '../components/AudioSocketBotParts'
import './Templates.css'

const blankForm = () => ({
    name: '',
    client_id: '',
    vicidial_ip: '',
    vicidial_port: 5060,
    server_ip_map: '',
    sip_user_start: '',
    sip_user_end: '',
    ra_user_start: '',
    ra_user_end: '',
    sip_secret: '',
    campaign: '',
    transfer_did: '',
    inbound_group: '',
    transfer_mode: '',
    closer_ip: '',
    closer_port: '',
    closer_did: '',
    vicidial_api_user: '',
    vicidial_api_pass: '',
    vicidial_api_url: '',
    vicidial_web_path: '',
    ra_api_default: false,
    avatar_id: '',
    quality_avatar_id: '',
    quantity_avatar_id: '',
    voice_set_id: '',
    lxc_cores: 4,
    lxc_memory_mb: 2048,
    lxc_disk_gb: 10
})

function Templates() {
    // Data states
    const [templates, setTemplates] = useState([])
    const [clients, setClients] = useState([])
    const [avatars, setAvatars] = useState([])
    const [voiceSets, setVoiceSets] = useState([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)

    // Filter
    const [searchQuery, setSearchQuery] = useState('')

    // Modal states
    const [showModal, setShowModal] = useState(false)
    const [modalMode, setModalMode] = useState('add')
    const [currentTemplate, setCurrentTemplate] = useState(null)
    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState(null)
    const [showAdvanced, setShowAdvanced] = useState(false)

    // Form data
    const [formData, setFormData] = useState(blankForm())
    // Multi-server editing state (drives the Vicidial IP "+" list + structured
    // Server IP map). These sync INTO formData.vicidial_ip (first IP = primary)
    // and formData.server_ip_map (serialized "START-END:IP" lines), so the save
    // path is unchanged.
    const [ipList, setIpList] = useState([''])
    const [mapRows, setMapRows] = useState([])

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [cloningBots, setCloningBots] = useState(null)    // template to clone bot LXCs from
    const [cloneResult, setCloneResult] = useState(null)
    const [templateToDelete, setTemplateToDelete] = useState(null)

    // API test (reachable / auth / in-group) — tests the typed form values.
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState(null)
    // Web-path auto-detect
    const [detecting, setDetecting] = useState(false)
    const [detectResult, setDetectResult] = useState(null)

    async function handleDetectWebPath() {
        setDetecting(true)
        setDetectResult(null)
        try {
            const body = {
                vicidial_api_url:  (formData.vicidial_api_url  || '').trim(),
                vicidial_web_path: (formData.vicidial_web_path || '').trim(),
                vicidial_ip:       (formData.vicidial_ip       || '').trim(),
            }
            const r = await audiosocketBotsApi.detectWebPath(currentTemplate?.id, body)
            const d = r?.data || r
            setDetectResult(d)
            if (d?.ok && d.web_path) setFormData(f => ({ ...f, vicidial_web_path: d.web_path }))
        } catch (e) {
            setDetectResult({ ok: false, error: e?.response?.data?.error || e.message })
        } finally {
            setDetecting(false)
        }
    }

    async function handleTestApi() {
        setTesting(true)
        setTestResult(null)
        try {
            const body = {
                vicidial_api_url:  (formData.vicidial_api_url  || '').trim(),
                vicidial_api_user: (formData.vicidial_api_user || '').trim(),
                vicidial_api_pass: (formData.vicidial_api_pass || '').trim(),
                vicidial_web_path: (formData.vicidial_web_path || '').trim(),
                inbound_group:     (formData.inbound_group     || '').trim(),
                vicidial_ip:       (formData.vicidial_ip       || '').trim(),
            }
            const r = await audiosocketBotsApi.testTemplateApi(currentTemplate?.id, body)
            setTestResult(r?.data || r)
        } catch (e) {
            setTestResult({ ok: false, error: e?.response?.data?.error || e.message })
        } finally {
            setTesting(false)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        try {
            const [tplRes, clientsRes, avatarsRes, voiceSetsRes] = await Promise.all([
                audiosocketBotsApi.listTemplates(),
                audiosocketBotsApi.listClients(),
                audiosocketBotsApi.listAvatars(),
                audiosocketBotsApi.listVoiceSets()
            ])

            setTemplates(Array.isArray(tplRes.data?.templates) ? tplRes.data.templates : [])
            setClients(Array.isArray(clientsRes.data?.clients) ? clientsRes.data.clients : [])
            setAvatars(Array.isArray(avatarsRes.data?.avatars) ? avatarsRes.data.avatars : [])
            setVoiceSets(Array.isArray(voiceSetsRes.data?.voice_sets) ? voiceSetsRes.data.voice_sets : [])
        } catch (error) {
            console.error('Failed to load templates data:', error)
        } finally {
            setLoading(false)
        }
    }

    const refresh = async () => {
        setRefreshing(true)
        await loadData()
        setRefreshing(false)
    }

    const sipCount = (start, end) => {
        const s = parseInt(start)
        const e = parseInt(end)
        if (isNaN(s) || isNaN(e) || e < s) return 0
        return e - s + 1
    }

    // ── Multi-server helpers ──────────────────────────────────────────────
    const parseMapText = (text) => (text || '').split(/[\n;,]+/).map(l => {
        const m = l.trim().match(/^(\d+)\s*-\s*(\d+)\s*(?:->|=>|=|:)\s*(\S+)$/)
        return m ? { start: m[1], end: m[2], ip: m[3] } : null
    }).filter(Boolean)

    const deriveServerArrays = (vicidialIp, mapText) => {
        const rows = parseMapText(mapText)
        const ips = []
        const add = (ip) => { ip = (ip || '').trim(); if (ip && !ips.includes(ip)) ips.push(ip) }
        add(vicidialIp)
        rows.forEach(r => add(r.ip))
        return { ipList: ips.length ? ips : [''], mapRows: rows }
    }

    // Set the arrays AND mirror them into formData (vicidial_ip = first IP,
    // server_ip_map = serialized map, only when 2+ IPs).
    const applyServers = (newIpList, newMapRows) => {
        setIpList(newIpList)
        setMapRows(newMapRows)
        const nonEmpty = newIpList.map(s => (s || '').trim()).filter(Boolean)
        const primary = (newIpList[0] || '').trim()
        const mapText = nonEmpty.length > 1
            ? newMapRows.filter(r => r.start && r.end && r.ip)
                .map(r => `${String(r.start).trim()}-${String(r.end).trim()}:${String(r.ip).trim()}`).join('\n')
            : ''
        setFormData(fd => ({ ...fd, vicidial_ip: primary, server_ip_map: mapText }))
    }

    // Additional (non-primary) IPs that no complete Server IP map range uses.
    const unmappedIps = () => {
        const used = new Set(mapRows.filter(r => r.start && r.end && r.ip).map(r => String(r.ip).trim()))
        return ipList.slice(1).map(s => (s || '').trim()).filter(ip => ip && !used.has(ip))
    }

    const openAddModal = () => {
        setFormData(blankForm())
        setIpList([''])
        setMapRows([])
        setShowAdvanced(false)
        setModalMode('add')
        setCurrentTemplate(null)
        setSaveError(null)
        setShowModal(true)
    }

    const fillFormFromTemplate = (t) => ({
        name: t.name || '',
        client_id: t.client_id ? String(t.client_id) : '',
        vicidial_ip: t.vicidial_ip || '',
        server_ip_map: t.server_ip_map || '',
        vicidial_port: t.vicidial_port || 5060,
        sip_user_start: t.sip_user_start ?? '',
        sip_user_end: t.sip_user_end ?? '',
        ra_user_start: t.ra_user_start ?? '',
        ra_user_end: t.ra_user_end ?? '',
        sip_secret: t.sip_secret || '',
        campaign: t.campaign || '',
        transfer_did: t.transfer_did || '',
        inbound_group: t.inbound_group || '',
        transfer_mode: t.transfer_mode || '',
        closer_ip: t.closer_ip || '',
        closer_port: t.closer_port ?? '',
        closer_did: t.closer_did || '',
        vicidial_api_user: t.vicidial_api_user || '',
        vicidial_api_pass: t.vicidial_api_pass || '',
        vicidial_api_url: t.vicidial_api_url || '',
        vicidial_web_path: t.vicidial_web_path || '',
        ra_api_default: !!Number(t.ra_api_default),
        avatar_id: t.avatar_id ? String(t.avatar_id) : '',
        quality_avatar_id: t.quality_avatar_id ? String(t.quality_avatar_id) : '',
        quantity_avatar_id: t.quantity_avatar_id ? String(t.quantity_avatar_id) : '',
        voice_set_id: t.voice_set_id ? String(t.voice_set_id) : '',
        lxc_cores: t.lxc_cores || 4,
        lxc_memory_mb: t.lxc_memory_mb || 2048,
        lxc_disk_gb: t.lxc_disk_gb || 10
    })

    const openEditModal = (template) => {
        setFormData(fillFormFromTemplate(template))
        { const d = deriveServerArrays(template.vicidial_ip, template.server_ip_map); setIpList(d.ipList); setMapRows(d.mapRows) }
        setShowAdvanced(false)
        setModalMode('edit')
        setCurrentTemplate(template)
        setSaveError(null)
        setShowModal(true)
    }

    const cloneTemplate = (template) => {
        // Copy config into a new template — clear SIP range so user picks fresh one
        setFormData({
            ...fillFormFromTemplate(template),
            name: `${template.name} (Copy)`,
            sip_user_start: '',
            sip_user_end: '',
            ra_user_start: '',
            ra_user_end: ''
        })
        { const d = deriveServerArrays(template.vicidial_ip, template.server_ip_map); setIpList(d.ipList); setMapRows(d.mapRows) }
        setShowAdvanced(false)
        setModalMode('add')
        setCurrentTemplate(null)
        setSaveError(null)
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setCurrentTemplate(null)
        setSaveError(null)
    }

    const handleSave = async () => {
        const required = ['name', 'vicidial_ip', 'sip_user_start', 'sip_user_end', 'avatar_id']
        for (const k of required) {
            if (formData[k] === '' || formData[k] === null || formData[k] === undefined) {
                setSaveError(`Missing required field: ${k}`)
                return
            }
        }
        const start = parseInt(formData.sip_user_start)
        const end = parseInt(formData.sip_user_end)
        if (isNaN(start) || isNaN(end) || end < start) {
            setSaveError('SIP range end must be ≥ start')
            return
        }
        // Extra dialler IPs are only stored through their phone ranges — one
        // with no complete range would silently disappear on save.
        const unmapped = unmappedIps()
        if (unmapped.length) {
            setSaveError(`Map a phone range to ${unmapped.join(', ')} in the Server IP map (or remove ${unmapped.length === 1 ? 'that IP' : 'those IPs'}) — otherwise ${unmapped.length === 1 ? 'it' : 'they'} won't be saved.`)
            return
        }

        setSaving(true)
        setSaveError(null)
        try {
            const data = {
                name: formData.name,
                client_id: formData.client_id ? parseInt(formData.client_id) : null,
                vicidial_ip: formData.vicidial_ip,
                vicidial_port: parseInt(formData.vicidial_port) || 5060,
                server_ip_map: (formData.server_ip_map || '').trim() || null,
                sip_user_start: start,
                sip_user_end: end,
                ra_user_start: formData.ra_user_start !== '' ? parseInt(formData.ra_user_start) : null,
                ra_user_end: formData.ra_user_end !== '' ? parseInt(formData.ra_user_end) : null,
                sip_secret: formData.sip_secret || null,
                campaign: formData.campaign || null,
                transfer_did: formData.transfer_did || null,
                inbound_group: (formData.inbound_group || '').trim() || null,
                transfer_mode: (formData.transfer_mode || '').trim() || null,
                closer_ip: (formData.closer_ip || '').trim() || null,
                closer_port: formData.closer_port !== '' ? parseInt(formData.closer_port) : null,
                closer_did: (formData.closer_did || '').trim() || null,
                vicidial_api_user: (formData.vicidial_api_user || '').trim() || null,
                vicidial_api_pass: (formData.vicidial_api_pass || '').trim() || null,
                vicidial_api_url:  (formData.vicidial_api_url  || '').trim() || null,
                vicidial_web_path: (formData.vicidial_web_path || '').trim() || null,
                ra_api_default: formData.ra_api_default ? 1 : 0,
                avatar_id: parseInt(formData.avatar_id),
                quality_avatar_id: formData.quality_avatar_id ? parseInt(formData.quality_avatar_id) : null,
                quantity_avatar_id: formData.quantity_avatar_id ? parseInt(formData.quantity_avatar_id) : null,
                voice_set_id: formData.voice_set_id ? parseInt(formData.voice_set_id) : null,
                lxc_cores: parseInt(formData.lxc_cores) || 4,
                lxc_memory_mb: parseInt(formData.lxc_memory_mb) || 2048,
                lxc_disk_gb: parseInt(formData.lxc_disk_gb) || 10
            }

            if (modalMode === 'edit' && currentTemplate) {
                await audiosocketBotsApi.updateTemplate(currentTemplate.id, data)
            } else {
                await audiosocketBotsApi.createTemplate(data)
            }

            closeModal()
            loadData()
        } catch (error) {
            console.error('Failed to save template:', error)
            setSaveError(error.response?.data?.error || error.message)
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!templateToDelete) return
        try {
            await audiosocketBotsApi.deleteTemplate(templateToDelete.id)
            setShowDeleteModal(false)
            setTemplateToDelete(null)
            loadData()
        } catch (error) {
            console.error('Failed to delete template:', error)
            alert('Error deleting template: ' + (error.response?.data?.error || error.message))
        }
    }

    const filteredTemplates = templates.filter(t => {
        const q = searchQuery.toLowerCase()
        if (!q) return true
        return (
            (t.name || '').toLowerCase().includes(q) ||
            (t.client_name || '').toLowerCase().includes(q) ||
            (t.vicidial_ip || '').toLowerCase().includes(q) ||
            (t.campaign || '').toLowerCase().includes(q)
        )
    })

    const totalSips = templates.reduce(
        (sum, t) => sum + (t.sip_count || sipCount(t.sip_user_start, t.sip_user_end)),
        0
    )
    const totalActiveMachines = templates.reduce((sum, t) => sum + (t.active_machines || 0), 0)
    const withAvatarCount = templates.filter(t => t.avatar_id).length

    if (loading) {
        return (
            <div className="templates-page">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="templates-page">
            {/* Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-title">Templates</h1>
                    <p className="page-subtitle">
                        AudioSocket bot configurations — used by the Servers page to bulk-clone bots
                    </p>
                </div>
                <div className="page-header-actions">
                    <button
                        onClick={refresh}
                        className="btn btn-secondary btn-icon"
                        disabled={refreshing}
                    >
                        <RefreshCw size={18} className={refreshing ? 'spinner' : ''} />
                    </button>
                    <button onClick={openAddModal} className="btn btn-primary">
                        <Plus size={18} />
                        New Template
                    </button>
                </div>
            </div>

            {/* Stats Bar */}
            <div className="stat-blocks">
                <div className="stat-block blue">
                    <div className="stat-block-icon"><Layers size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{templates.length}</span>
                        <span className="stat-block-label">Templates</span>
                    </div>
                </div>
                <div className="stat-block green">
                    <div className="stat-block-icon"><Server size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{totalSips}</span>
                        <span className="stat-block-label">Total SIPs</span>
                    </div>
                </div>
                <div className="stat-block purple">
                    <div className="stat-block-icon"><Cpu size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{totalActiveMachines}</span>
                        <span className="stat-block-label">Active Machines</span>
                    </div>
                </div>
                <div className="stat-block orange">
                    <div className="stat-block-icon"><UserCircle size={20} /></div>
                    <div className="stat-block-content">
                        <span className="stat-block-value">{withAvatarCount}</span>
                        <span className="stat-block-label">With Avatar</span>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="toolbar">
                <div className="search-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, client, Vicidial IP, campaign..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                </div>
            </div>

            {/* Templates Grid */}
            {filteredTemplates.length > 0 ? (
                <div className="templates-grid">
                    {filteredTemplates.map(template => {
                        const sips = template.sip_count || sipCount(template.sip_user_start, template.sip_user_end)
                        return (
                            <motion.div
                                key={template.id}
                                className="template-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <div className="template-card-header">
                                    <div>
                                        <h3 className="template-card-title">{template.name}</h3>
                                        <p className="template-card-description">
                                            SIP {template.sip_user_start}–{template.sip_user_end} on {template.vicidial_ip}:{template.vicidial_port}
                                        </p>
                                    </div>
                                    <div className="template-card-actions">
                                        <button
                                            onClick={() => setCloningBots(template)}
                                            className="btn btn-ghost btn-icon"
                                            title="Clone bots — create bot LXCs from this template on a server"
                                            style={{ color: '#3b82f6' }}
                                        >
                                            <Boxes size={16} />
                                        </button>
                                        <button
                                            onClick={() => openEditModal(template)}
                                            className="btn btn-ghost btn-icon"
                                            title="Edit"
                                        >
                                            <Edit size={16} />
                                        </button>
                                        <button
                                            onClick={() => cloneTemplate(template)}
                                            className="btn btn-ghost btn-icon"
                                            title="Clone Template"
                                            style={{ color: '#8b5cf6' }}
                                        >
                                            <Copy size={16} />
                                        </button>
                                        <button
                                            onClick={() => {
                                                setTemplateToDelete(template)
                                                setShowDeleteModal(true)
                                            }}
                                            className="btn btn-ghost btn-icon"
                                            title="Delete"
                                            style={{ color: '#ef4444' }}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="template-card-body">
                                    <div className="template-stat">
                                        <Server size={16} className="template-stat-icon" />
                                        <span className="template-stat-value">{sips}</span>
                                        <span>SIPs in pool</span>
                                    </div>
                                    <div className="template-stat">
                                        <Cpu size={16} className="template-stat-icon" />
                                        <span className="template-stat-value">{template.active_machines || 0}</span>
                                        <span>active machines</span>
                                    </div>
                                    <div className="template-stat">
                                        <UserCircle size={16} className="template-stat-icon" />
                                        <span>{template.avatar_name || <em style={{ opacity: 0.5 }}>No avatar</em>}</span>
                                    </div>
                                    <div className="template-stat">
                                        <Mic size={16} className="template-stat-icon" />
                                        <span>{template.voice_set_name || <em style={{ opacity: 0.5 }}>From avatar</em>}</span>
                                    </div>
                                </div>

                                <div className="template-card-footer">
                                    {template.client_name && (
                                        <span className="template-tag">
                                            <Building2 size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                                            {template.client_name}
                                        </span>
                                    )}
                                    {template.campaign && (
                                        <span className="template-tag">Campaign {template.campaign}</span>
                                    )}
                                    {template.transfer_did && (
                                        <span className="template-tag">
                                            <PhoneCall size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                                            DID {template.transfer_did}
                                        </span>
                                    )}
                                </div>
                            </motion.div>
                        )
                    })}
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <FileText size={32} />
                    </div>
                    <h3 className="empty-state-title">No templates found</h3>
                    <p className="empty-state-text">Create your first template to get started</p>
                    <button onClick={openAddModal} className="btn btn-primary" style={{ marginTop: '16px' }}>
                        <Plus size={18} />
                        Create First Template
                    </button>
                </div>
            )}

            {/* Add/Edit Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                    >
                        <motion.div
                            className="modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2 className="modal-title">
                                    {modalMode === 'edit' ? 'Edit Template' : 'New Template'}
                                </h2>
                                <button onClick={closeModal} className="modal-close">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* BASIC */}
                                <div className="form-group">
                                    <label className="form-label">
                                        Template Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., FE Campaign — Hetzner-1"
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Client</label>
                                    <SearchableDropdown
                                        options={clients}
                                        value={formData.client_id}
                                        onChange={(val) => setFormData({ ...formData, client_id: val })}
                                        placeholder="(none)"
                                        labelKey="client_name"
                                        valueKey="id"
                                    />
                                </div>

                                {/* VICIDIAL primary server + port — aligned pair */}
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Vicidial IP <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={ipList[0] || ''}
                                            onChange={(e) => { const n = [...ipList]; n[0] = e.target.value; applyServers(n, mapRows) }}
                                            placeholder="192.155.100.74"
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vicidial Port</label>
                                        <input
                                            type="number"
                                            value={formData.vicidial_port}
                                            onChange={(e) => setFormData({ ...formData, vicidial_port: e.target.value })}
                                            placeholder="5060"
                                            className="form-input"
                                        />
                                    </div>
                                </div>

                                {/* Additional dialler servers (full-width, flows below the pair) */}
                                <div className="form-group">
                                    {ipList.slice(1).map((ip, idx) => {
                                        const i = idx + 1
                                        return (
                                            <div key={i} className="inline-row">
                                                <input
                                                    type="text"
                                                    value={ip}
                                                    onChange={(e) => { const n = [...ipList]; n[i] = e.target.value; applyServers(n, mapRows) }}
                                                    placeholder="additional server IP"
                                                    className="form-input"
                                                />
                                                <button type="button" title="remove IP" className="icon-btn"
                                                    onClick={() => applyServers(ipList.filter((_, j) => j !== i), mapRows)}>
                                                    <X size={15} />
                                                </button>
                                            </div>
                                        )
                                    })}
                                    <button type="button" className="add-row-btn"
                                        onClick={() => applyServers([...ipList, ''], mapRows)}>
                                        <Plus size={14} /> Add server IP
                                    </button>
                                    <p className="form-hint">Add a server only if this client's dialler runs on multiple servers — then map phone ranges to each below.</p>
                                    {unmappedIps().length > 0 && (
                                        <p className="form-hint" style={{ color: 'var(--warning)' }}>
                                            {unmappedIps().join(', ')} {unmappedIps().length === 1 ? 'has' : 'have'} no phone range yet — add one in the Server IP map below, or {unmappedIps().length === 1 ? 'it' : 'they'} won't be saved.
                                        </p>
                                    )}
                                </div>

                                {/* Server IP map — only shows when 2+ server IPs */}
                                {ipList.map(s => (s || '').trim()).filter(Boolean).length > 1 && (
                                    <div className="form-group">
                                        <label className="form-label">Server IP map <span className="label-hint">— which phones go to which server (mirror the dialler)</span></label>
                                        <div className="map-row map-row-head">
                                            <span>Phone start</span>
                                            <span>Phone end</span>
                                            <span>Server IP</span>
                                            <span className="icon-btn-spacer" />
                                        </div>
                                        {mapRows.map((row, i) => (
                                            <div key={i} className="map-row">
                                                <input type="number" value={row.start} placeholder="5000" className="form-input"
                                                    onChange={(e) => { const n = [...mapRows]; n[i] = { ...n[i], start: e.target.value }; applyServers(ipList, n) }} />
                                                <input type="number" value={row.end} placeholder="5009" className="form-input"
                                                    onChange={(e) => { const n = [...mapRows]; n[i] = { ...n[i], end: e.target.value }; applyServers(ipList, n) }} />
                                                <select value={row.ip} className="form-input"
                                                    onChange={(e) => { const n = [...mapRows]; n[i] = { ...n[i], ip: e.target.value }; applyServers(ipList, n) }}>
                                                    <option value="">Server IP…</option>
                                                    {ipList.map(s => (s || '').trim()).filter(Boolean).map(ip => (
                                                        <option key={ip} value={ip}>{ip}</option>
                                                    ))}
                                                </select>
                                                <button type="button" title="remove range" className="icon-btn"
                                                    onClick={() => applyServers(ipList, mapRows.filter((_, j) => j !== i))}>
                                                    <X size={15} />
                                                </button>
                                            </div>
                                        ))}
                                        <button type="button" className="add-row-btn"
                                            onClick={() => applyServers(ipList, [...mapRows, { start: '', end: '', ip: '' }])}>
                                            <Plus size={14} /> Add range
                                        </button>
                                        <p className="form-hint">
                                            Each phone range registers to its server. Phones not in any range use the primary (first) IP. Takes effect on the next clone / Asterisk regen.
                                        </p>
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Phones Start <span className="required">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.sip_user_start}
                                            onChange={(e) => setFormData({ ...formData, sip_user_start: e.target.value })}
                                            placeholder="5000"
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">
                                            Phones End <span className="required">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.sip_user_end}
                                            onChange={(e) => setFormData({ ...formData, sip_user_end: e.target.value })}
                                            placeholder="5004"
                                            className="form-input"
                                        />
                                    </div>
                                </div>

                                {formData.sip_user_start !== '' && formData.sip_user_end !== '' && (
                                    <div className={`sip-preview ${
                                        sipCount(formData.sip_user_start, formData.sip_user_end) > 0 ? 'valid' : 'invalid'
                                    }`} style={{ marginTop: '-8px', marginBottom: 'var(--space-lg)' }}>
                                        {sipCount(formData.sip_user_start, formData.sip_user_end) > 0
                                            ? `✓ ${sipCount(formData.sip_user_start, formData.sip_user_end)} SIPs in this range`
                                            : '✗ End must be ≥ start'}
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">RA User Start <span className="label-hint">(Remote Agent)</span></label>
                                        <input
                                            type="number"
                                            value={formData.ra_user_start}
                                            onChange={(e) => setFormData({ ...formData, ra_user_start: e.target.value })}
                                            placeholder="3133"
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">RA User End</label>
                                        <input
                                            type="number"
                                            value={formData.ra_user_end}
                                            onChange={(e) => setFormData({ ...formData, ra_user_end: e.target.value })}
                                            placeholder="3142"
                                            className="form-input"
                                        />
                                    </div>
                                </div>
                                {formData.ra_user_start !== '' && formData.ra_user_end !== '' && (
                                    <div className={`sip-preview ${
                                        sipCount(formData.ra_user_start, formData.ra_user_end) === sipCount(formData.sip_user_start, formData.sip_user_end) && sipCount(formData.ra_user_start, formData.ra_user_end) > 0 ? 'valid' : 'invalid'
                                    }`} style={{ marginTop: '-8px', marginBottom: 'var(--space-lg)' }}>
                                        {sipCount(formData.ra_user_start, formData.ra_user_end) <= 0
                                            ? '✗ RA End must be ≥ RA Start'
                                            : sipCount(formData.ra_user_start, formData.ra_user_end) === sipCount(formData.sip_user_start, formData.sip_user_end)
                                                ? `✓ ${sipCount(formData.ra_user_start, formData.ra_user_end)} RAs — phone ${formData.sip_user_start} → RA ${formData.ra_user_start}, phone ${formData.sip_user_end} → RA ${formData.ra_user_end}`
                                                : `✗ RA count (${sipCount(formData.ra_user_start, formData.ra_user_end)}) must equal phone count (${sipCount(formData.sip_user_start, formData.sip_user_end)})`}
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">Phone Registration Password</label>
                                    <input
                                        type="text"
                                        value={formData.sip_secret}
                                        onChange={(e) => setFormData({ ...formData, sip_secret: e.target.value })}
                                        placeholder="SipPass5000xyz (Vicidial Phones → Registration Password)"
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Campaign</label>
                                        <input
                                            type="text"
                                            value={formData.campaign}
                                            onChange={(e) => setFormData({ ...formData, campaign: e.target.value })}
                                            placeholder="001"
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Transfer DID <span className="label-hint">(optional)</span></label>
                                        <input
                                            type="text"
                                            value={formData.transfer_did}
                                            onChange={(e) => setFormData({ ...formData, transfer_did: e.target.value })}
                                            placeholder="53000  (leave blank if using API transfer)"
                                            className="form-input"
                                        />
                                    </div>
                                </div>

                                {/* VICIDIAL AGENT API — used for ra_call_control transfer release.
                                    Per-cluster creds; one row per client = one Vicidial.
                                    Enable per-bot on Licenses page (Vicidial RA User + Use RA API checkbox). */}
                                <div className="form-divider" style={{ marginTop: 'var(--space-md)' }}>
                                    <p style={{ margin: '0 0 8px 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                        <strong>Vicidial Agent API</strong> — credentials for <code>ra_call_control</code> (transfer release).
                                        Required only for bots whose Licenses row has "Use Vicidial RA API" checked.
                                    </p>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Vicidial API User</label>
                                        <input
                                            type="text"
                                            value={formData.vicidial_api_user}
                                            onChange={(e) => setFormData({ ...formData, vicidial_api_user: e.target.value })}
                                            placeholder="Bots-admin"
                                            className="form-input"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vicidial API Password</label>
                                        <input
                                            type="password"
                                            value={formData.vicidial_api_pass}
                                            onChange={(e) => setFormData({ ...formData, vicidial_api_pass: e.target.value })}
                                            placeholder="(alphanumeric only)"
                                            className="form-input"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Vicidial API URL (optional)</label>
                                    <input
                                        type="text"
                                        value={formData.vicidial_api_url}
                                        onChange={(e) => setFormData({ ...formData, vicidial_api_url: e.target.value })}
                                        placeholder="https://devnitysolution.flexodialer.com  (defaults to https://<Vicidial IP> if blank)"
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Vicidial API web path <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>(optional — only if Vicidial is on a non-standard dir)</span></label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            type="text"
                                            value={formData.vicidial_web_path}
                                            onChange={(e) => setFormData({ ...formData, vicidial_web_path: e.target.value })}
                                            placeholder="/vicidial  (e.g. /RRBjYCm for obfuscated/hosted installs — leave blank for standard)"
                                            className="form-input"
                                            style={{ flex: 1 }}
                                        />
                                        <button type="button" className="btn btn-secondary" onClick={handleDetectWebPath}
                                            disabled={detecting || !(formData.vicidial_api_url || formData.vicidial_ip)}
                                            title="Probe the dialer and auto-fill the working path">
                                            {detecting ? <Loader2 size={14} className="spin" /> : <Search size={14} />} Detect
                                        </button>
                                    </div>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                        The directory that serves <code>non_agent_api.php</code>. Standard Vicidial = <code>/vicidial</code>.
                                        Hosted/hardened dialers relocate it (the random path in the admin URL, e.g. <code>/RRBjYCm</code>).
                                        Blank = try <code>/vicidial</code>. <strong>Tip:</strong> paste their admin URL in the API URL field above, then click Detect.
                                    </p>
                                    {detectResult && (
                                        <p style={{ margin: '6px 0 0 0', fontSize: '0.74rem', color: detectResult.ok ? '#16a34a' : '#dc2626' }}>
                                            {detectResult.ok
                                                ? <>✓ Found working path <code>{detectResult.web_path}</code>{detectResult.version ? ` (Vicidial ${detectResult.version})` : ''} — filled in above.</>
                                                : <>✗ {detectResult.error || 'No working path found'}{Array.isArray(detectResult.tried) && detectResult.tried.length ? ` — tried: ${detectResult.tried.map(t => `${t.web_path} (${t.http})`).join(', ')}` : ''}</>}
                                        </p>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Closer location <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>(where the verifier/closer lives)</span></label>
                                    <select
                                        value={formData.transfer_mode}
                                        onChange={(e) => setFormData({ ...formData, transfer_mode: e.target.value })}
                                        className="form-input"
                                    >
                                        <option value="">Auto (infer: in-group → INGROUPTRANSFER, else DID)</option>
                                        <option value="csd">Same dialler (CSD) — INGROUPTRANSFER to the in-group</option>
                                        <option value="cod">Different dialler (COD) — EXTENSIONTRANSFER via a transfer extension</option>
                                    </select>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                        <strong>Same dialler:</strong> closers are agents on THIS Vicidial → in-group transfer.&nbsp;
                                        <strong>Different dialler:</strong> closers are on a SEPARATE Vicidial → the call hops via a DID/extension
                                        (you can't in-group-transfer across two Vicidials).
                                    </p>
                                </div>

                                {formData.transfer_mode === 'cod' && (
                                    <div style={{ borderLeft: '3px solid var(--text-muted)', paddingLeft: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '0.78rem' }}><strong>Closer-dialler (COD) details</strong> — generates the dialplan line for the bots' dialler.</p>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">Transfer extension <span className="label-hint">(on bots' dialler)</span></label>
                                                <input type="text" value={formData.transfer_did} onChange={(e) => setFormData({ ...formData, transfer_did: e.target.value })} placeholder="4455" className="form-input" />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Closer dialler IP</label>
                                                <input type="text" value={formData.closer_ip} onChange={(e) => setFormData({ ...formData, closer_ip: e.target.value })} placeholder="168.119.237.106" className="form-input" />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">Closer SIP port</label>
                                                <input type="text" value={formData.closer_port} onChange={(e) => setFormData({ ...formData, closer_port: e.target.value })} placeholder="7788" className="form-input" />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Closer DID / inbound number</label>
                                                <input type="text" value={formData.closer_did} onChange={(e) => setFormData({ ...formData, closer_did: e.target.value })} placeholder="12894789781" className="form-input" />
                                            </div>
                                        </div>
                                        {formData.transfer_did && formData.closer_ip && formData.closer_port && formData.closer_did && (
                                            <div style={{ marginTop: 6 }}>
                                                <p style={{ margin: '0 0 4px 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>Give this to the BOTS' dialler team (Admin → Servers → Custom Dialplan Entry, then reload):</p>
                                                <code style={{ display: 'block', padding: '8px', background: '#1e1e1e', color: '#9cdcfe', borderRadius: 6, fontSize: '0.78rem', wordBreak: 'break-all' }}>
                                                    exten =&gt; {formData.transfer_did},1,Dial(SIP/{formData.closer_ip}:{formData.closer_port}/{formData.closer_did})
                                                </code>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">Transfer In-group <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>(verifiers/closers — INGROUPTRANSFER default for all bots in this client)</span></label>
                                    <input
                                        type="text"
                                        value={formData.inbound_group}
                                        onChange={(e) => setFormData({ ...formData, inbound_group: e.target.value })}
                                        placeholder="e.g. Closers / fecloser  (leave blank to transfer to the DID instead)"
                                        className="form-input"
                                    />
                                    <p style={{ margin: '4px 0 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                        Set once here = where qualified calls are routed for the whole client. A per-bot
                                        "RA API In-group" on the Licenses page overrides this for that bot only.
                                    </p>
                                </div>

                                {/* TEST API — checks the typed values (URL/user/pass/web-path/in-group) live */}
                                <div className="form-group">
                                    <button
                                        type="button"
                                        onClick={handleTestApi}
                                        disabled={testing}
                                        style={{
                                            fontSize: '0.82rem', fontWeight: 600, padding: '8px 16px', borderRadius: 8,
                                            border: '1px solid var(--accent, #3b82f6)', cursor: testing ? 'wait' : 'pointer',
                                            background: 'rgba(59,130,246,0.10)', color: 'var(--accent, #3b82f6)'
                                        }}>
                                        {testing ? 'Testing…' : '🔌 Test API connection'}
                                    </button>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                        Tests the values above (no need to save first): is the dialer reachable, do the
                                        credentials authenticate, and is the in-group reachable.
                                    </p>
                                    {testResult && (() => {
                                        const r = testResult
                                        const Row = ({ label, state, note }) => {
                                            const color = state === 'ok' ? '#22c55e' : state === 'bad' ? '#ef4444' : state === 'warn' ? '#f59e0b' : '#94a3b8'
                                            const icon  = state === 'ok' ? '✓' : state === 'bad' ? '✗' : state === 'warn' ? '!' : '–'
                                            return (
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: '0.8rem', padding: '2px 0' }}>
                                                    <span style={{ color, fontWeight: 700, width: 14 }}>{icon}</span>
                                                    <span style={{ minWidth: 130 }}>{label}</span>
                                                    <span style={{ color: 'var(--text-muted)' }}>{note}</span>
                                                </div>
                                            )
                                        }
                                        const reachState = r.reachable === true ? 'ok' : r.reachable === false ? 'bad' : 'na'
                                        const authState  = r.auth_ok === true ? 'ok' : r.auth_ok === false ? 'bad' : 'na'
                                        const agents = r.ingroup_agents
                                        const igState = !r.in_group ? 'na'
                                            : r.ingroup_ok !== true ? 'bad'
                                            : (agents > 0 ? 'ok' : 'warn')
                                        const igNote = !r.in_group ? 'none entered'
                                            : r.ingroup_ok !== true ? 'no response'
                                            : agents > 0 ? `${agents} agent${agents === 1 ? '' : 's'} logged in${r.ingroup_queue ? `, ${r.ingroup_queue} in queue` : ''}`
                                            : '0 agents logged in — verify name & that verifiers are logged in'
                                        // Overall banner: ok only if reachable+auth+(no in-group OR agents>0)
                                        const overallOk = r.reachable && r.auth_ok && (!r.in_group || agents > 0)
                                        return (
                                            <div style={{
                                                marginTop: 10, padding: '10px 12px', borderRadius: 8,
                                                border: '1px solid ' + (overallOk ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'),
                                                background: overallOk ? 'rgba(34,197,94,0.07)' : 'rgba(239,68,68,0.07)'
                                            }}>
                                                <Row label="Reachable" state={reachState}
                                                     note={r.version ? `Vicidial ${r.version}${r.build ? ' / ' + r.build : ''}${r.latency_ms != null ? ` · ${r.latency_ms}ms` : ''}` : (r.host || '')} />
                                                <Row label="Credentials" state={authState}
                                                     note={authState === 'ok' ? `user "${r.api_user || ''}" authenticated` : authState === 'bad' ? 'rejected' : 'not checked'} />
                                                <Row label={`In-group${r.in_group ? ` "${r.in_group}"` : ''}`} state={igState} note={igNote} />
                                                {r.error && <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#fca5a5' }}>{r.error}</div>}
                                                {r.note && !r.error && <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.note}</div>}
                                                {r.ingroup_detail && <div style={{ marginTop: 6, fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(r.ingroup_detail).slice(0, 200)}</div>}
                                            </div>
                                        )
                                    })()}
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.ra_api_default}
                                            onChange={(e) => setFormData({ ...formData, ra_api_default: e.target.checked })}
                                        />
                                        Use Vicidial RA API for all bots in this client
                                    </label>
                                    <p style={{ margin: '4px 0 0 24px', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                        Enables the ra_call_control transfer path for every bot here. Each bot still needs
                                        its own "Vicidial RA User" on the Licenses page (RA users differ per phone); a bot
                                        with no RA user safely falls back to DID transfer.
                                    </p>
                                </div>

                                {/* BOT CONFIG */}
                                <div className="form-group">
                                    <label className="form-label">
                                        Avatar <span className="required">*</span>
                                    </label>
                                    <SearchableDropdown
                                        options={avatars}
                                        value={formData.avatar_id}
                                        onChange={(val) => setFormData({ ...formData, avatar_id: val })}
                                        placeholder="Select Avatar..."
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                </div>

                                {/* Quality / Quantity — the client picks between these two from
                                    their portal. Leave blank to hide the switch for them. */}
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Quality avatar</label>
                                        <SearchableDropdown
                                            options={avatars}
                                            value={formData.quality_avatar_id}
                                            onChange={(val) => setFormData({ ...formData, quality_avatar_id: val })}
                                            placeholder="(not offered)"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Quantity avatar</label>
                                        <SearchableDropdown
                                            options={avatars}
                                            value={formData.quantity_avatar_id}
                                            onChange={(val) => setFormData({ ...formData, quantity_avatar_id: val })}
                                            placeholder="(not offered)"
                                            labelKey="name"
                                            valueKey="id"
                                        />
                                    </div>
                                </div>
                                <p style={{ margin: '-4px 0 12px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    Set both to let this client switch their bots between quality and
                                    quantity from the portal. Switching restarts the bot, so a call it is
                                    on at that moment drops.
                                </p>

                                <div className="form-group">
                                    <label className="form-label">Voice Set</label>
                                    <SearchableDropdown
                                        options={voiceSets}
                                        value={formData.voice_set_id}
                                        onChange={(val) => setFormData({ ...formData, voice_set_id: val })}
                                        placeholder="(use avatar's voice set)"
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                </div>

                                {/* ADVANCED — LXC resources */}
                                <div className="form-divider">
                                    <button
                                        type="button"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="btn btn-ghost"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        {showAdvanced ? '▼ Hide LXC Resources' : '▶ LXC Resources (advanced)'}
                                    </button>
                                </div>

                                {showAdvanced && (
                                    <div className="bot-config-section">
                                        <h4 style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
                                            LXC Resources (per cloned bot)
                                        </h4>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">Cores</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={formData.lxc_cores}
                                                    onChange={(e) => setFormData({ ...formData, lxc_cores: e.target.value })}
                                                    className="form-input"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Memory (MB)</label>
                                                <input
                                                    type="number"
                                                    min="512"
                                                    value={formData.lxc_memory_mb}
                                                    onChange={(e) => setFormData({ ...formData, lxc_memory_mb: e.target.value })}
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Disk (GB)</label>
                                            <input
                                                type="number"
                                                min="5"
                                                value={formData.lxc_disk_gb}
                                                onChange={(e) => setFormData({ ...formData, lxc_disk_gb: e.target.value })}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>
                                )}

                                {saveError && (
                                    <div className="sip-preview invalid" style={{ marginTop: 'var(--space-md)' }}>
                                        ✗ {saveError}
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button onClick={closeModal} className="btn btn-secondary">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="btn btn-primary"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 size={16} className="spinner" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={16} />
                                            {modalMode === 'edit' ? 'Save Changes' : 'Create Template'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Clone bots (LXCs) from a template */}
            {cloningBots && (
                <CloneModal
                    template={cloningBots}
                    onClose={() => setCloningBots(null)}
                    onDone={(r) => { setCloneResult(r); setCloningBots(null); loadData() }}
                />
            )}
            {cloneResult && <CloneResult result={cloneResult} onClose={() => setCloneResult(null)} />}

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowDeleteModal(false)}
                    >
                        <motion.div
                            className="modal delete-modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-body">
                                <div className="delete-modal-icon">
                                    <Trash2 size={24} />
                                </div>
                                <h3 className="delete-modal-title">Delete Template?</h3>
                                <p className="delete-modal-text">
                                    Are you sure you want to delete "{templateToDelete?.name}"?
                                    This archives the template; existing machines stay on the host.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="btn btn-danger"
                                >
                                    <Trash2 size={16} />
                                    Delete
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default Templates
